import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { SEO } from "@/components/seo";
import { 
  TrendingUp, Zap, Shield, Bot, BarChart3, Clock, 
  CheckCircle2, ArrowRight, Play, LineChart, Wallet,
  Building2, Users, Globe, Cpu, MousePointerClick, TestTube, Rocket
} from "lucide-react";
import { useAuth } from "@/lib/auth-context";

const brokerLogos = [
  { name: "Alpaca", color: "#FFD100" },
  { name: "Tradier", color: "#00A4E4" },
  { name: "Coinbase", color: "#0052FF" },
  { name: "Kraken", color: "#5741D9" },
  { name: "Interactive Brokers", color: "#DC143C" },
];

const features = [
  {
    icon: Bot,
    title: "Automated Trading",
    description: "Create powerful trading algorithms without writing a single line of code. Set rules and let the system execute trades automatically.",
  },
  {
    icon: LineChart,
    title: "Multi-Asset Trading",
    description: "Trade stocks, options, ETFs, and cryptocurrencies all from one unified platform with real-time market data.",
  },
  {
    icon: Zap,
    title: "News & Social Triggers",
    description: "React to market-moving events in milliseconds. Connect to news feeds, social media, and custom webhooks.",
  },
  {
    icon: Shield,
    title: "Risk Controls",
    description: "Built-in safety features including position limits, daily trade caps, and paper trading mode for strategy testing.",
  },
  {
    icon: BarChart3,
    title: "Portfolio Analytics",
    description: "Track your performance with real-time P&L, allocation breakdowns, and comprehensive trade history.",
  },
  {
    icon: Clock,
    title: "24/7 Execution",
    description: "Your algorithms run around the clock, never missing opportunities even when you're away from your screen.",
  },
];

const howItWorks = [
  {
    step: 1,
    icon: MousePointerClick,
    title: "Build",
    description: "Create custom trading rules with our intuitive builder. Select assets, define conditions, and set your trading parameters.",
    color: "text-chart-1",
  },
  {
    step: 2,
    icon: TestTube,
    title: "Test",
    description: "Run your strategies in paper trading mode. Validate your rules with real market data before going live.",
    color: "text-chart-2",
  },
  {
    step: 3,
    icon: Rocket,
    title: "Trade",
    description: "Connect your brokerage account and start automated trading. Monitor performance and adjust strategies on the fly.",
    color: "text-chart-3",
  },
];

const getStartedSteps = [
  {
    icon: Users,
    title: "Apply Online",
    description: "Complete our streamlined application in about 10 minutes with your personal and financial information.",
  },
  {
    icon: Shield,
    title: "Verify Identity",
    description: "Submit a valid driver's license, state ID, or passport for quick identity verification.",
  },
  {
    icon: Wallet,
    title: "Fund Your Account",
    description: "Deposit funds via ACH bank transfer, wire, or transfer from an existing brokerage account.",
  },
  {
    icon: Play,
    title: "Start Trading",
    description: "Build your first trading algorithm and start executing trades automatically.",
  },
];

const pricingPlans = [
  {
    name: "Starter",
    price: "Free",
    period: "",
    description: "Perfect for learning and paper trading",
    features: [
      "Paper trading only",
      "3 active trading rules",
      "Basic market data",
      "Email support",
      "Community access",
    ],
    cta: "Get Started",
    popular: false,
  },
  {
    name: "Pro",
    price: "$49",
    period: "/month",
    description: "For active traders ready to automate",
    features: [
      "Live trading enabled",
      "25 active trading rules",
      "Real-time market data",
      "News & social triggers",
      "Priority support",
      "Advanced analytics",
    ],
    cta: "Start Free Trial",
    popular: true,
  },
  {
    name: "Enterprise",
    price: "Custom",
    period: "",
    description: "For institutions and professional traders",
    features: [
      "Unlimited trading rules",
      "Dedicated account manager",
      "Custom integrations",
      "API access",
      "White-label options",
      "SLA guarantees",
    ],
    cta: "Contact Sales",
    popular: false,
  },
];

export default function LandingPage() {
  const { isAuthenticated } = useAuth();
  const [, setLocation] = useLocation();

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="TradePro - Automated Trading Platform | No Coding Required"
        description="Create powerful trading algorithms without coding. Automate stocks, options, and crypto trading with real-time market data and event-driven triggers."
      />
      <header className="fixed top-0 left-0 right-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Link href="/" data-testid="link-home-logo">
            <div className="flex items-center gap-2 cursor-pointer">
              <div className="h-8 w-8 rounded-md bg-primary flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-primary-foreground" />
              </div>
              <span className="text-xl font-bold">TradePro</span>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-6" data-testid="nav-main">
            <Link href="/features" className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="link-nav-features">Features</Link>
            <Link href="/pricing" className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="link-nav-pricing">Pricing</Link>
            <Link href="/about" className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="link-nav-about">About</Link>
          </nav>
          <div className="flex items-center gap-3">
            {isAuthenticated ? (
              <Link href="/dashboard">
                <Button data-testid="button-go-to-dashboard">Go to Dashboard</Button>
              </Link>
            ) : (
              <>
                <Button variant="ghost" data-testid="button-login" onClick={() => setLocation("/login")}>Log in</Button>
                <Button data-testid="button-get-started" onClick={() => setLocation("/login")}>Get Started</Button>
              </>
            )}
          </div>
        </div>
      </header>

      <main>
        <section className="pt-32 pb-20 px-4">
          <div className="container mx-auto text-center max-w-4xl">
            <Badge variant="secondary" className="mb-6">
              Automated Trading Made Simple
            </Badge>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              Never Miss a Trade.
              <br />
              <span className="text-primary">No Coding Required.</span>
            </h1>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              TradePro revolutionizes trading by letting you create powerful algorithms without coding. 
              Automate decisions, track multi-asset portfolios, and trade with institutional-grade speed and accuracy.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="w-full sm:w-auto" data-testid="button-hero-cta" onClick={() => setLocation("/login")}>
                Try it Free
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Link href="#how-it-works">
                <Button size="lg" variant="outline" className="w-full sm:w-auto" data-testid="button-learn-more">
                  Learn More
                </Button>
              </Link>
            </div>
          </div>
        </section>

        <section className="py-12 border-y bg-muted/30">
          <div className="container mx-auto px-4">
            <p className="text-center text-sm text-muted-foreground mb-6">Enabled brokers and data sources</p>
            <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12">
              {brokerLogos.map((broker) => (
                <div key={broker.name} className="flex items-center gap-2 text-muted-foreground">
                  <Building2 className="h-5 w-5" style={{ color: broker.color }} />
                  <span className="font-medium">{broker.name}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="how-it-works" className="py-20 px-4">
          <div className="container mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">How It Works</h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Build, test, and trade live with algorithms — without writing any code
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {howItWorks.map((item) => (
                <div key={item.step} className="text-center" data-testid={`how-it-works-step-${item.step}`}>
                  <div className={`inline-flex items-center justify-center h-16 w-16 rounded-full bg-muted mb-6`}>
                    <item.icon className={`h-8 w-8 ${item.color}`} />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{item.title}</h3>
                  <p className="text-muted-foreground">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 px-4 bg-muted/30">
          <div className="container mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Powerful Features</h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Everything you need to automate your trading strategy
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {features.map((feature) => (
                <Card key={feature.title} className="hover-elevate" data-testid={`feature-card-${feature.title.toLowerCase().replace(/\s+/g, '-')}`}>
                  <CardHeader>
                    <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                      <feature.icon className="h-6 w-6 text-primary" />
                    </div>
                    <CardTitle className="text-lg">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{feature.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 px-4">
          <div className="container mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Get Started Today</h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                It's easy to open an account and start automated trading
              </p>
            </div>
            <div className="grid md:grid-cols-4 gap-8 max-w-5xl mx-auto">
              {getStartedSteps.map((step, index) => (
                <div key={step.title} className="text-center relative" data-testid={`get-started-step-${index + 1}`}>
                  <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-primary/10 mb-4">
                    <step.icon className="h-6 w-6 text-primary" />
                  </div>
                  <Badge variant="outline" className="absolute top-0 right-1/4 -translate-y-2">
                    Step {index + 1}
                  </Badge>
                  <h3 className="text-lg font-semibold mb-2">{step.title}</h3>
                  <p className="text-sm text-muted-foreground">{step.description}</p>
                </div>
              ))}
            </div>
            <div className="text-center mt-12">
              <Link href="/open-account">
                <Button size="lg" data-testid="button-open-account">
                  Open New Account
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        <section id="pricing" className="py-20 px-4 bg-muted/30">
          <div className="container mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Simple, Transparent Pricing</h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Choose the plan that fits your trading needs
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {pricingPlans.map((plan) => (
                <Card 
                  key={plan.name} 
                  className={`relative ${plan.popular ? 'border-primary shadow-lg' : ''}`}
                  data-testid={`pricing-plan-${plan.name.toLowerCase()}`}
                >
                  {plan.popular && (
                    <Badge className="absolute -top-3 left-1/2 -translate-x-1/2">
                      Most Popular
                    </Badge>
                  )}
                  <CardHeader className="text-center pb-2">
                    <CardTitle className="text-xl">{plan.name}</CardTitle>
                    <CardDescription>{plan.description}</CardDescription>
                    <div className="pt-4">
                      <span className="text-4xl font-bold">{plan.price}</span>
                      <span className="text-muted-foreground">{plan.period}</span>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <ul className="space-y-3">
                      {plan.features.map((feature) => (
                        <li key={feature} className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                  <CardFooter>
                    <div className="w-full">
                      <Button 
                        className="w-full" 
                        variant={plan.popular ? "default" : "outline"}
                        data-testid={`button-plan-${plan.name.toLowerCase()}`}
                        onClick={() => setLocation("/login")}
                      >
                        {plan.cta}
                      </Button>
                    </div>
                  </CardFooter>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 px-4">
          <div className="container mx-auto text-center max-w-3xl">
            <Globe className="h-12 w-12 mx-auto mb-6 text-primary" />
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Launch?</h2>
            <p className="text-xl text-muted-foreground mb-8">
              Join thousands of traders who are already automating their strategies with TradePro.
            </p>
            <Link href="/open-account">
              <Button size="lg" data-testid="button-final-cta">
                Open New Account
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </section>
      </main>

      <footer className="border-t py-12 px-4 bg-muted/30">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src="/logo.png" alt="Tradeharbor.io" className="h-10 w-auto" />
              </div>
              <p className="text-sm text-muted-foreground">
                AI-powered trading platform with seamless brokerage integration.
              </p>
              <p className="text-xs text-muted-foreground mt-4">
                Brokerage services provided by Alpaca Markets, Inc. Member FINRA/SIPC.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link href="/features" className="hover:text-foreground transition-colors" data-testid="link-footer-features">Features</Link></li>
                <li><Link href="/pricing" className="hover:text-foreground transition-colors" data-testid="link-footer-pricing">Pricing</Link></li>
                <li><Link href="/open-account" className="hover:text-foreground transition-colors" data-testid="link-footer-open-account">Open Account</Link></li>
                <li><Link href="/sentiment" className="hover:text-foreground transition-colors">AI Sentiment Analysis</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link href="/about" className="hover:text-foreground transition-colors" data-testid="link-footer-about">About</Link></li>
                <li><a href="mailto:careers@tradeharbor.io" className="hover:text-foreground transition-colors" data-testid="link-footer-careers">Careers</a></li>
                <li><a href="mailto:support@tradeharbor.io" className="hover:text-foreground transition-colors" data-testid="link-footer-contact">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link href="/privacy" className="hover:text-foreground transition-colors" data-testid="link-footer-privacy">Privacy Policy</Link></li>
                <li><Link href="/terms" className="hover:text-foreground transition-colors" data-testid="link-footer-terms">Terms of Service</Link></li>
                <li><Link href="/disclosures" className="hover:text-foreground transition-colors" data-testid="link-footer-disclosures">Disclosures</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t mt-8 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-muted-foreground">
              <p>&copy; {new Date().getFullYear()} Tradeharbor.io. All rights reserved.</p>
              <div className="flex gap-4">
                <a href="https://alpaca.markets" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">
                  Powered by Alpaca
                </a>
                <a href="https://anthropic.com" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">
                  AI by Claude
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
